import React from 'react';
import {
  ArrowLeft,
  Sparkles,
  CheckCircle,
  ExternalLink,
  Shirt,
  Wind,
  Clock,
  Beaker,
} from 'lucide-react';

interface SevenCLPromoViewProps {
  userCheckInCount: number;
  onBack: () => void;
  onVisitSite: () => void;
}

const SevenCLPromoView: React.FC<SevenCLPromoViewProps> = ({
  userCheckInCount,
  onBack,
  onVisitSite,
}) => {
  const features = [
    {
      icon: <Beaker className="w-5 h-5" />,
      title: 'AI-Formulated Science',
      description: 'Advanced molecular trapping technology eliminates smoke odors at the source',
    },
    {
      icon: <Shirt className="w-5 h-5" />,
      title: 'Wardrobe Protection',
      description: 'Keep your favorite clothes smoke-free without dry cleaning',
    },
    {
      icon: <Wind className="w-5 h-5" />,
      title: 'The Purge Protocol',
      description: 'Simple overnight process restores your garments completely',
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: 'Reusable System',
      description: 'Each sachet works for multiple uses - sustainable and economical',
    },
  ];

  const howItWorks = [
    'Seal your smoke-exposed clothing in the TPU Barrier Bag',
    'Insert the 7CL-AI-v1 Sachet',
    'Shake for 30 seconds to distribute',
    'Leave overnight - wake up to fresh clothes',
  ];

  return (
    <div className="flex flex-col h-full bg-stone-900">
      {/* Header with gradient */}
      <div className="relative bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-stone-900 pb-8">
        {/* Back button */}
        <button
          onClick={onBack}
          className="absolute top-4 left-4 p-2 bg-black/30 backdrop-blur rounded-full z-10"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>

        <div className="pt-16 px-6 text-center">
          {/* Logo placeholder */}
          <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/20">
            <Sparkles className="w-10 h-10 text-white" />
          </div>

          <h1 className="text-3xl font-bold text-white mb-2">7CL</h1>
          <p className="text-amber-400 font-medium">Seven Cent Line</p>
          <p className="text-stone-400 text-sm mt-2">
            AI-Powered Post-Smoke Restoration
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 -mt-4">
        {/* Personalized message */}
        {userCheckInCount > 0 && (
          <div className="p-4 bg-gradient-to-r from-amber-500/10 to-transparent border border-amber-500/20 rounded-xl mb-6">
            <p className="text-sm text-white">
              <span className="text-amber-500 font-semibold">{userCheckInCount} lounge visits</span>{' '}
              this month! Your clothes have seen some smoke. Time to discover the solution.
            </p>
          </div>
        )}

        {/* Hero pitch */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-white mb-3">
            Enjoy Your Cigar. We'll Handle the Rest.
          </h2>
          <p className="text-stone-400 leading-relaxed">
            No more choosing between your favorite cigars and your favorite clothes.
            7CL's revolutionary restoration system eliminates smoke odors overnight,
            so you can light up without holding back.
          </p>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="p-4 bg-stone-800/50 rounded-xl"
            >
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center text-amber-500 mb-3">
                {feature.icon}
              </div>
              <h3 className="text-white font-medium text-sm mb-1">{feature.title}</h3>
              <p className="text-stone-500 text-xs">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">The Purge Protocol</h3>
          <div className="space-y-3">
            {howItWorks.map((step, index) => (
              <div key={index} className="flex gap-3">
                <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-stone-900 text-sm font-bold flex-shrink-0">
                  {index + 1}
                </div>
                <p className="text-stone-300 text-sm pt-0.5">{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* What's in the kit */}
        <div className="p-4 bg-stone-800/30 rounded-xl mb-6">
          <h3 className="text-white font-medium mb-3">The 7CL Kit Includes:</h3>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-sm text-stone-300">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              7CL-AI-v1 Sachet (Bamboo Charcoal + Zeolite + Beta-Cyclodextrin)
            </li>
            <li className="flex items-center gap-2 text-sm text-stone-300">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              TPU Barrier Bag (premium garment protection)
            </li>
            <li className="flex items-center gap-2 text-sm text-stone-300">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              Quick-start guide with care instructions
            </li>
          </ul>
        </div>

        {/* Cigar lounge tie-in */}
        <div className="p-4 bg-gradient-to-r from-stone-800 to-stone-800/50 rounded-xl mb-6 border border-stone-700">
          <div className="flex items-start gap-3">
            <div className="text-2xl">🚬</div>
            <div>
              <h4 className="text-white font-medium mb-1">Perfect for Lounge Regulars</h4>
              <p className="text-stone-400 text-sm">
                Stop worrying about smoke clinging to your jacket. Enjoy your
                lounge time knowing your clothes will be fresh by morning.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="pb-8">
          <button
            onClick={onVisitSite}
            className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-stone-900 rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20"
          >
            Visit 7centline.com
            <ExternalLink className="w-4 h-4" />
          </button>
          <p className="text-center text-stone-500 text-xs mt-3">
            Limited production - Join the waitlist for early access
          </p>
        </div>
      </div>
    </div>
  );
};

export default SevenCLPromoView;
